import { QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { FileRecord } from "@/shared/domain/files";
import {
  FileNode,
  FileSystemNodeType,
  isFile,
} from "@/shared/domain/files/filesystem";

import { fileQueryKeys } from "./fileQueryKeys";
import {
  baseName,
  cancelAndSnapshotDirectoryContents,
  DirectoryContentsCache,
  dirPath,
  restoreDirectoryContentsSnapshot,
  transformDirectoryContentsCache,
} from "./moveDirectoryContentsCache";

type MoveContext = {
  snapshot: [QueryKey, DirectoryContentsCache | undefined][];
};

interface MoveFileProps {
  fileId: string;
  datasetId: string;
  /** The file's current relative_path within the dataset (e.g. "subdir/file.txt"). */
  currentPath: string;
  newFullPath: string;
}

export function useMoveFileMutation() {
  const { files: fileService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<FileRecord, Error, MoveFileProps, MoveContext>({
    mutationFn: ({ fileId, datasetId, newFullPath }) => {
      return fileService.moveFile(fileId, datasetId, newFullPath);
    },
    onMutate: async ({ fileId, datasetId, currentPath, newFullPath }) => {
      const sourceDir = dirPath(currentPath);
      const destDir = dirPath(newFullPath);

      const snapshot = await cancelAndSnapshotDirectoryContents(
        queryClient,
        datasetId,
        [sourceDir, destDir],
      );

      // Remove from source, capturing the node so we can reinsert at destination
      let movedNode: FileNode | undefined;
      transformDirectoryContentsCache(
        queryClient,
        datasetId,
        sourceDir,
        (items) =>
          items.filter((n) => {
            if (isFile(n) && n.file.file_id === fileId) {
              movedNode = n;
              return false;
            }
            return true;
          }),
      );

      // Optimistically insert at destination
      if (movedNode && sourceDir !== destDir) {
        const optimisticNode: FileNode = {
          type: FileSystemNodeType.File,
          name: baseName(newFullPath),
          file: { ...movedNode.file, relative_path: newFullPath },
        };
        transformDirectoryContentsCache(
          queryClient,
          datasetId,
          destDir,
          (items) => [...items, optimisticNode],
        );
      }

      return { snapshot };
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshot) {
        restoreDirectoryContentsSnapshot(queryClient, context.snapshot);
      }
    },
    onSuccess: (file) => {
      queryClient.setQueryData(fileQueryKeys.detail(file.file_id), file);
    },
    onSettled: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: fileQueryKeys.directoryItemCount(),
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: fileQueryKeys.directoryContents(),
          exact: false,
        }),
      ]);
    },
  });
}
